/**
 * Report-view preference cookie (WSRADM-0051). Hidden column / series keys per
 * surface only — preference only, never a token, never bootstrap JSON.
 * Host-only (no Domain); readable by the SPA (not HttpOnly). Same attributes as
 * writeLocaleCookie.
 *
 * Value = URLSearchParams of `prefsKey → comma-joined keys`, in write order
 * (oldest first) so the size cap can drop the oldest-written surface.
 */

export const VIEW_PREFS_COOKIE = 'wsr_admin_views';

/** Encoded cookie value stays under this many characters. */
export const VIEW_PREFS_MAX_LENGTH = 3072;

/** Membership Stats fixed (non-band) column keys, in display order. */
export const MEMBERSHIP_FIXED_KEYS = [
  'lost',
  'lapsed',
  'netMembers',
  'netNew',
  'existing',
] as const;

/** Revenue family key (Revenue Stats + dashboard revenue chart). */
export function familyViewKey(slug: string | null): string {
  return `family:${slug ?? '__unassigned__'}`;
}

/** Duration band (Membership Stats) or check-ins band (dashboard) key. */
export function bandViewKey(key: string): string {
  return `band:${key}`;
}

/** Escape the list separator inside a key so `a,b` survives a round-trip. */
function encodeKey(key: string): string {
  return key.replace(/%/g, '%25').replace(/,/g, '%2C');
}

/** Comma-join keys (the `?hide=` and cookie wire format). */
export function joinViewKeys(keys: readonly string[]): string {
  return keys.map(encodeKey).join(',');
}

/** Split a comma-joined key list; empty segments and undecodable keys are dropped. */
export function splitViewKeys(raw: string): string[] {
  const keys: string[] = [];
  for (const part of raw.split(',')) {
    if (part.length === 0) continue;
    try {
      keys.push(decodeURIComponent(part));
    } catch {
      // A malformed key is ignored, never an error.
    }
  }
  return keys;
}

function readRawCookie(): string | null {
  if (typeof document === 'undefined') return null;
  const parts = document.cookie.split(';');
  for (const part of parts) {
    const trimmed = part.trim();
    const eq = trimmed.indexOf('=');
    if (eq === -1) continue;
    const name = trimmed.slice(0, eq);
    if (name !== VIEW_PREFS_COOKIE) continue;
    return trimmed.slice(eq + 1);
  }
  return null;
}

function readAll(): URLSearchParams | null {
  const raw = readRawCookie();
  if (raw === null || raw.length === 0) return null;
  try {
    return new URLSearchParams(decodeURIComponent(raw));
  } catch {
    return null;
  }
}

/** Hidden keys remembered for `prefsKey`, or null when none / malformed. */
export function readViewPref(prefsKey: string): string[] | null {
  try {
    const params = readAll();
    if (params === null) return null;
    const value = params.get(prefsKey);
    if (value === null) return null;
    return splitViewKeys(value);
  } catch {
    return null;
  }
}

/**
 * Remember `keys` for `prefsKey` (an empty list forgets the surface). The
 * surface moves to the end (newest); if the cookie would exceed the cap, the
 * oldest-written surfaces are dropped first.
 */
export function writeViewPref(prefsKey: string, keys: readonly string[]): void {
  if (typeof document === 'undefined') return;
  const params = readAll() ?? new URLSearchParams();
  params.delete(prefsKey);
  if (keys.length > 0) params.append(prefsKey, joinViewKeys(keys));

  let value = encodeURIComponent(params.toString());
  while (value.length > VIEW_PREFS_MAX_LENGTH) {
    const oldest = params.keys().next();
    if (oldest.done) break;
    params.delete(oldest.value);
    value = encodeURIComponent(params.toString());
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${VIEW_PREFS_COOKIE}=${value}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

/** Read a scalar preference string (e.g. `membershipCompare`, `revenueCompare`, `membershipFamilyDim`). */
export function readScalarViewPref(prefsKey: string): string | null {
  try {
    const params = readAll();
    if (params === null) return null;
    return params.get(prefsKey);
  } catch {
    return null;
  }
}

/** Remember a scalar preference string (null/empty removes it). */
export function writeScalarViewPref(prefsKey: string, value: string | null): void {
  if (typeof document === 'undefined') return;
  const params = readAll() ?? new URLSearchParams();
  params.delete(prefsKey);
  if (value !== null && value.length > 0) params.append(prefsKey, value);

  let encoded = encodeURIComponent(params.toString());
  while (encoded.length > VIEW_PREFS_MAX_LENGTH) {
    const oldest = params.keys().next();
    if (oldest.done) break;
    params.delete(oldest.value);
    encoded = encodeURIComponent(params.toString());
  }

  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${VIEW_PREFS_COOKIE}=${encoded}; Path=/; Max-Age=31536000; SameSite=Lax${secure}`;
}

